import { isSolveBoundViolationMessage } from '@shared/helpers/expression-solving.helpers';
import { TooltipProps } from '@ui/Tooltip';

/**
 * Formula errors stay hidden while a numeric override or a local parse error
 * is showing. A min/max failure stays visible: the stored override can be
 * inside the bounds while the expression is not.
 */
export const visibleExpressionError = (options: {
  errorMessage?: string;
  numberParseError?: Error;
  hasValueOverride: boolean;
  hasLocalError: boolean;
}): string | undefined => {
  if (options.numberParseError || options.hasLocalError) return undefined;
  if (!options.errorMessage) return undefined;
  if (
    options.hasValueOverride &&
    !isSolveBoundViolationMessage(options.errorMessage)
  ) {
    return undefined;
  }
  return options.errorMessage;
};

export type TooltipData = {
  location: 'wrapper' | 'expressionIcon';
  data: Omit<TooltipProps, 'children'>;
};

type GetTooltipDataParams = {
  /** Normal tooltip text */
  tooltip?: TooltipProps['title'];
  /** local ExpressionInput error */
  localError?: Error;
  /** expressionError, if any */
  expressionError?: string;
  /** Whether the component is active/focused */
  isActive: boolean;
  isHovered: boolean;
};

export const _getTooltipData = ({
  localError,
  expressionError,
  isActive,
  isHovered,
  tooltip,
}: GetTooltipDataParams): TooltipData => {
  // local error is prioritized
  const open = isActive || isHovered;
  if (localError) {
    return {
      location: 'wrapper',
      data: {
        title: localError.message,
        open,
      },
    };
  }
  if (expressionError) {
    return {
      location: 'expressionIcon',
      data: {
        title: expressionError,
        open,
        offset: 8, // so it ends up outside the input
      },
    };
  }
  if (tooltip) {
    return {
      location: 'wrapper',
      data: {
        title: tooltip,
        open: isHovered,
      },
    };
  }
  return {
    location: 'wrapper',
    data: { title: '', open: false },
  };
};
